import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./AdminLogsPage.css";

const formatDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso || "N/A";
  }
};

const formatAction = (action) => {
  if (!action) return "—";
  return action
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getMetadataSummary = (meta) => {
  if (!meta || typeof meta !== "object") return "—";
  const parts = [];
  if (meta.user_email) parts.push(meta.user_email);
  if (meta.document_type) parts.push(meta.document_type);
  if (meta.kyc_name) parts.push(meta.kyc_name);
  if (meta.reject_reason) {
    const r = meta.reject_reason;
    parts.push(r.length > 40 ? `${r.slice(0, 40)}…` : r);
  }
  return parts.length ? parts.join(" · ") : "—";
};

const AdminLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ admin_id: "", action: "", search: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      ...(filters.admin_id && { admin_id: filters.admin_id.trim() }),
      ...(filters.action && { action: filters.action.trim() }),
      ...(filters.search && { search: filters.search.trim() }),
    };
    const res = await AdminService.getAdminLogs(params);
    setLoading(false);
    if (res?.success) {
      setLogs(res.data || []);
      const pag = res.pagination || {};
      setPagination((prev) => ({
        ...prev,
        page: pag.page ?? prev.page,
        limit: pag.limit ?? prev.limit,
        total: pag.total ?? prev.total,
        totalPages: pag.total_pages ?? pag.totalPages ?? prev.totalPages,
      }));
    } else {
      setError(res?.message || "Failed to load admin logs.");
      setLogs([]);
    }
  }, [pagination.page, pagination.limit, filters.admin_id, filters.action, filters.search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({ admin_id: "", action: "", search: "" });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [filters.admin_id, filters.action, filters.search]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (e) => {
    const limit = Number(e.target.value) || 20;
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  };

  return (
    <main className="dashboard-main dashboard-subpage admin-logs-page">
      <section className="dashboard-hero admin-logs-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Admin Logs</span>
          </p>
          <h1 className="dashboard-hero__title">Admin Logs</h1>
          <p className="dashboard-hero__subtitle">View admin activity logs and audit trail</p>
        </div>
        <div className="admin-logs-hero__icon">
          <i className="fas fa-clipboard-list" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="admin-logs-section">
          <div className="admin-logs-header">
            <h2 className="admin-logs__title">
              <i className="fas fa-list-alt" /> Activity Logs
            </h2>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="admin-logs-filters">
            <div className="admin-logs-filters__row">
              <label className="admin-logs-filter">
                <span>Search</span>
                <input
                  type="text"
                  placeholder="User email, UUID, target..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                />
              </label>
              <label className="admin-logs-filter">
                <span>Admin ID</span>
                <input
                  type="text"
                  placeholder="Filter by admin ID"
                  value={filters.admin_id}
                  onChange={(e) => handleFilterChange("admin_id", e.target.value)}
                />
              </label>
              <label className="admin-logs-filter">
                <span>Action type</span>
                <input
                  type="text"
                  placeholder="e.g. KYC_DOCUMENT_APPROVED"
                  value={filters.action}
                  onChange={(e) => handleFilterChange("action", e.target.value)}
                />
              </label>
              <label className="admin-logs-filter">
                <span>Per page</span>
                <select
                  value={pagination.limit}
                  onChange={handleLimitChange}
                  className="admin-logs-select"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
              <button type="button" className="admin-logs-btn-apply" onClick={handleResetFilters}>
                <i className="fas fa-undo" /> Reset
              </button>
            </div>
          </form>

          {error && (
            <div className="admin-logs-error">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          <div className="admin-logs-table-wrap">
            {loading ? (
              <div className="admin-logs-loading">
                <i className="fas fa-spinner fa-spin" /> Loading logs...
              </div>
            ) : logs.length === 0 ? (
              <div className="admin-logs-empty">
                <i className="fas fa-inbox" />
                <p>No admin logs found.</p>
              </div>
            ) : (
              <table className="admin-logs-table">
                <thead>
                  <tr>
                    <th>Admin</th>
                    <th>Action</th>
                    <th>Details</th>
                    <th>IP</th>
                    <th>Status</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((row) => (
                    <tr key={row._id}>
                      <td>
                        <span className="admin-logs-admin">{row.admin_email || "—"}</span>
                        <span className="admin-logs-type">Type {row.admin_type ?? "—"}</span>
                      </td>
                      <td className="admin-logs-action">{formatAction(row.action)}</td>
                      <td className="admin-logs-details">{getMetadataSummary(row.metadata)}</td>
                      <td>{row.ip || "—"}</td>
                      <td>
                        <span
                          className={`admin-logs-status ${
                            row.status === "success" ? "admin-logs-status--success" : "admin-logs-status--error"
                          }`}
                        >
                          {row.status ?? "—"}
                        </span>
                      </td>
                      <td>{formatDateTime(row.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {pagination.totalPages > 1 && !loading && (
            <div className="admin-logs-pagination">
              <span className="admin-logs-pagination__info">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <div className="admin-logs-pagination__buttons">
                <button
                  type="button"
                  className="admin-logs-pagination__btn"
                  disabled={pagination.page <= 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  <i className="fas fa-chevron-left" /> Prev
                </button>
                <button
                  type="button"
                  className="admin-logs-pagination__btn"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next <i className="fas fa-chevron-right" />
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default AdminLogsPage;
